import React, { useState } from 'react';
import { FeedbackQuestionnaire } from '../components/feedback';
import { FiGift, FiMessageSquare } from 'react-icons/fi';
import PageHeader from '../components/layout/PageHeader';
import { pageDescriptions } from '../data/pageDescriptions';

const FeedbackPage = () => {
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const handleOpenQuestionnaire = () => {
    setShowQuestionnaire(true);
  };

  const handleCloseQuestionnaire = () => {
    setShowQuestionnaire(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <PageHeader
            title={pageDescriptions['/feedback'].title}
            description={pageDescriptions['/feedback'].description}
            whatYouCanDo={pageDescriptions['/feedback'].whatYouCanDo}
            whatYouShouldDo={pageDescriptions['/feedback'].whatYouShouldDo}
          />
        </div>

        {/* Main Content Card */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-8">
            <div className="flex items-center justify-center mb-6">
              <FiGift className="text-5xl mr-4" />
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-2">Earn 300 Credits!</h2>
                <p className="text-lg opacity-90">Complete our feedback survey and get rewarded</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Your Feedback Matters</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Help us understand what features work best for you</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Shape the future development of ResuMemo</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Improve the experience for all users</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Get early access to new features</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">What to Expect</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>5-section survey covering your experience</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Takes approximately 5-10 minutes to complete</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Questions about features, usability, and preferences</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Instant credit reward upon completion</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Your insights are valuable to us. Click below to begin the feedback survey and earn your credits.
              </p>
              <button
                onClick={handleOpenQuestionnaire}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white text-lg font-medium rounded-lg hover:from-green-600 hover:to-blue-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                <FiGift className="mr-3 text-xl" />
                Start Survey & Earn 300 Credits
              </button>
            </div>

            {/* Additional Info */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiMessageSquare className="text-blue-600 text-xl" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Anonymous</h4>
                  <p className="text-sm text-gray-600">Your responses are confidential and help us improve</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiGift className="text-green-600 text-xl" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Instant Reward</h4>
                  <p className="text-sm text-gray-600">Credits are added immediately after completion</p>
                </div>
                <div>
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary-600 text-xl font-bold">5</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Quick Survey</h4>
                  <p className="text-sm text-gray-600">Only 5 sections with relevant questions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Questionnaire Modal */}
      <FeedbackQuestionnaire 
        isOpen={showQuestionnaire} 
        onClose={handleCloseQuestionnaire} 
      />
    </div>
  );
};

export default FeedbackPage;